import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useCreateProjectService } from '@/services/projects';
import { useState, type ChangeEvent, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateProjectDialog: FC<CreateProjectDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const navigate = useNavigate();
  const { createProject, isCreating } = useCreateProjectService();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleCreate = () => {
    if (!name.trim()) return;

    createProject(
      { name: name.trim(), description: description.trim() || undefined },
      {
        onSuccess: data => {
          onOpenChange(false);
          setName('');
          setDescription('');
          navigate(data.id);
        },
        onError: () => {
          toast.error('Failed to create project');
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Project</DialogTitle>
        </DialogHeader>
        <FlexCol gap="4">
          <Input
            label="Name"
            placeholder="Project name"
            value={name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
          />
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">Description</label>
            <Textarea
              placeholder="Describe what this project is about..."
              value={description}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                setDescription(e.target.value)
              }
              rows={3}
            />
          </div>
        </FlexCol>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || isCreating}
            loading={isCreating}
          >
            Create Project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
